import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { kycApi } from '@eshop/shared'
import { useAuthStore } from '../../store/auth'
import type { VendorKYC } from '../../types'

export function KYCPending() {
  const [kyc, setKyc] = useState<VendorKYC | null>(null)
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()
  const { logout } = useAuthStore()

  const handleLogout = async () => {
    await logout()
    navigate('/login')
  }

  useEffect(() => {
    loadKYC()
  }, [])

  const loadKYC = async () => {
    try {
      setLoading(true)
      const data = await kycApi.getMyKYC()
      setKyc(data)

      // Redirect if not in pending status
      if (data.status === 'approved') {
        navigate('/backoffice')
      } else if (data.status === 'rejected') {
        navigate('/kyc/rejected')
      } else if (data.status === 'incomplete') {
        navigate('/kyc')
      }
    } catch (err: any) {
      console.error('Error loading KYC:', err)
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-start justify-center py-8 px-4 sm:px-6 lg:px-8">
      <div className="w-full max-w-3xl">
        {/* Logout Button */}
        <div className="flex justify-end mb-4">
          <button
            onClick={handleLogout}
            className="px-4 py-2 text-sm bg-gray-200 text-gray-700 font-medium rounded-lg hover:bg-gray-300 transition-colors flex items-center gap-2"
          >
            <span>🚪</span>
            Cerrar Sesión
          </button>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6 text-center">
          {/* Icon */}
          <div className="mx-auto w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mb-4">
            <svg className="w-8 h-8 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>

          {/* Title */}
          <h1 className="text-2xl font-bold text-gray-900 mb-3">
            Verificación en Proceso
          </h1>

          {/* Message */}
          <p className="text-base text-gray-700 mb-6">
            Tu solicitud de verificación ha sido enviada exitosamente y está siendo revisada por nuestro equipo.
          </p>

          {/* Status Card */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6 text-left">
            <h3 className="font-semibold text-amber-900 mb-2 text-sm">Estado Actual:</h3>
            <div className="space-y-2 text-sm text-amber-800">
              <div className="flex items-center">
                <svg className="w-4 h-4 mr-2 text-amber-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span>Solicitud recibida: {new Date(kyc?.submitted_at || '').toLocaleDateString('es-MX', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}</span>
              </div>
              <div className="flex items-center">
                <svg className="w-4 h-4 mr-2 text-amber-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                </svg>
                <span>Tiempo estimado de revisión: 2-3 días hábiles</span>
              </div>
            </div>
          </div>

          {/* Information */}
          <div className="text-left space-y-4">
            <h3 className="font-semibold text-gray-900">¿Qué sigue?</h3>
            <ul className="space-y-3 text-sm text-gray-600">
              <li className="flex items-start">
                <span className="mr-2 text-blue-600">1.</span>
                <span>Nuestro equipo revisará cuidadosamente toda la información y documentación proporcionada</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2 text-blue-600">2.</span>
                <span>Si necesitamos información adicional, nos pondremos en contacto contigo por correo electrónico</span>
              </li>
              <li className="flex items-start">
                <span className="mr-2 text-blue-600">3.</span>
                <span>Una vez aprobada tu cuenta, recibirás una notificación y podrás comenzar a vender</span>
              </li>
            </ul>
          </div>

          {/* Actions */}
          <div className="mt-6 pt-4 border-t">
            <button
              onClick={() => navigate('/')}
              className="px-6 py-2.5 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors text-sm shadow-sm"
            >
              Volver al inicio
            </button>
          </div>

          {/* Support */}
          <p className="mt-4 text-sm text-gray-600">
            ¿Tienes preguntas? Contacta a{' '}
            <a href="mailto:support@eshop.com" className="text-blue-600 hover:underline font-medium">
              support@eshop.com
            </a>
          </p>
        </div>
      </div>
    </div>
  )
}
